import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// Types
export interface MedRequest {
  id: string;
  tenantId: string;
  userId: string;
  userName: string;
  originalTransactionId: string;
  originalTransactionDate: string;
  originalAmount: number;
  requestedAmount: number;
  reason: 'FRAUD' | 'OPERATIONAL_FAILURE';
  description: string;
  status: 'PENDING' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'EXPIRED' | 'COMPLETED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  rejectionReason?: string;
  refundTransactionId?: string;
  refundedAt?: string;
  documents: MedDocument[];
  statusHistory: MedStatusHistory[];
  createdAt: string;
  updatedAt: string;
  daysRemaining?: number;
}

export interface MedDocument {
  id: string;
  medRequestId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  filePath: string;
  uploadedBy: string;
  uploadedByName: string;
  createdAt: string;
}

export interface MedStatusHistory {
  id: string;
  medRequestId: string;
  previousStatus?: string;
  newStatus: string;
  changedBy: string;
  changedByName: string;
  notes?: string;
  createdAt: string;
}

export interface CreateMedRequestDto {
  originalTransactionId: string;
  requestedAmount: number;
  reason: 'FRAUD' | 'OPERATIONAL_FAILURE';
  description: string;
}

export interface MedQueryDto {
  status?: string;
  reason?: string;
  priority?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface MedListResponse {
  data: MedRequest[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface MedStats {
  total: number;
  pending: number;
  underReview: number;
  approved: number;
  rejected: number;
  expired: number;
  completed: number;
  totalRequestedAmount: number;
  totalRefundedAmount: number;
  avgProcessingDays: number;
}

// Customer Hooks
export function useMyMedRequests(query: MedQueryDto = {}) {
  return useQuery<MedListResponse>({
    queryKey: ['my-med-requests', query],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query.status) params.append('status', query.status);
      if (query.startDate) params.append('startDate', query.startDate);
      if (query.endDate) params.append('endDate', query.endDate);
      if (query.page) params.append('page', String(query.page));
      if (query.limit) params.append('limit', String(query.limit));

      const response = await api.get(`/banking/pix/med?${params.toString()}`);
      return response.data;
    },
  });
}

export function useMedRequestDetail(id: string) {
  return useQuery<MedRequest>({
    queryKey: ['med-request', id],
    queryFn: async () => {
      const response = await api.get(`/banking/pix/med/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateMedRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMedRequestDto) => {
      const response = await api.post('/banking/pix/med', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-med-requests'] });
    },
  });
}

export function useUploadMedDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, files }: { id: string; files: File[] }) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      const response = await api.post(`/banking/pix/med/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['med-request', id] });
    },
  });
}

// Compliance Hooks
export function useMedQueue(query: MedQueryDto = {}) {
  return useQuery<MedListResponse>({
    queryKey: ['med-queue', query],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query.status) params.append('status', query.status);
      if (query.reason) params.append('reason', query.reason);
      if (query.priority) params.append('priority', query.priority);
      if (query.startDate) params.append('startDate', query.startDate);
      if (query.endDate) params.append('endDate', query.endDate);
      if (query.page) params.append('page', String(query.page));
      if (query.limit) params.append('limit', String(query.limit));

      const response = await api.get(`/banking/pix/med/compliance/queue?${params.toString()}`);
      return response.data;
    },
  });
}

export function useMedStats() {
  return useQuery<MedStats>({
    queryKey: ['med-stats'],
    queryFn: async () => {
      const response = await api.get('/banking/pix/med/compliance/stats');
      return response.data;
    },
  });
}

export function useApproveMedRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const response = await api.post(`/banking/pix/med/compliance/${id}/approve`, { notes });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['med-queue'] });
      queryClient.invalidateQueries({ queryKey: ['med-stats'] });
    },
  });
}

export function useRejectMedRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason, notes }: { id: string; reason: string; notes?: string }) => {
      const response = await api.post(`/banking/pix/med/compliance/${id}/reject`, { reason, notes });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['med-queue'] });
      queryClient.invalidateQueries({ queryKey: ['med-stats'] });
    },
  });
}

export function useRequestMedDocuments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, documentTypes, message }: { id: string; documentTypes: string[]; message?: string }) => {
      const response = await api.post(`/banking/pix/med/compliance/${id}/request-documents`, {
        documentTypes,
        message,
      });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['med-request', id] });
    },
  });
}

// Helper functions
export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    PENDING: 'Pendente',
    UNDER_REVIEW: 'Em Análise',
    APPROVED: 'Aprovado',
    REJECTED: 'Rejeitado',
    EXPIRED: 'Expirado',
    COMPLETED: 'Concluído',
  };
  return labels[status] || status;
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    PENDING: 'yellow',
    UNDER_REVIEW: 'blue',
    APPROVED: 'green',
    REJECTED: 'red',
    EXPIRED: 'gray',
    COMPLETED: 'green',
  };
  return colors[status] || 'gray';
}

export function getReasonLabel(reason: string): string {
  const labels: Record<string, string> = {
    FRAUD: 'Fraude',
    OPERATIONAL_FAILURE: 'Falha Operacional',
  };
  return labels[reason] || reason;
}

export function getPriorityLabel(priority: string): string {
  const labels: Record<string, string> = {
    LOW: 'Baixa',
    MEDIUM: 'Média',
    HIGH: 'Alta',
    CRITICAL: 'Crítica',
  };
  return labels[priority] || priority;
}

export function getPriorityColor(priority: string): string {
  const colors: Record<string, string> = {
    LOW: 'gray',
    MEDIUM: 'blue',
    HIGH: 'orange',
    CRITICAL: 'red',
  };
  return colors[priority] || 'gray';
}
